import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import moment from 'moment';
import { BehaviorSubject, Observable, Subscription, forkJoin, of } from 'rxjs';
import HashMap from 'src/app/types/hash-map';
import {
  DELETE_SUBSTATUS_DATE,
  GET_SUBSTATUS_DATES,
  UPDATE_SUBSTATUS,
  UPDATE_SUBSTATUS_DATE,
} from './graphql/substatuses';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import ProjectSubstatusDate from '../../../interfaces/project-substatus-date';
import { getProjectTargetOptions } from '@angular/cdk/schematics';

@Injectable({
  providedIn: 'root',
})
export class SubStatusesService {
  private _projectSubstatuses: any[] = [];
  private _projectSubstatusDates: any[] = [];
  private _subsequences: any[];
  private savingProjectSubstatuses: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private activeSequences: HashMap<BehaviorSubject<any[]>> = {};
  private projectSubstatuses: BehaviorSubject<any[]> = new BehaviorSubject<
    any[]
  >(this._projectSubstatuses);
  private projectSubstatusDates: BehaviorSubject<any[]> = new BehaviorSubject<
    any[]
  >(this._projectSubstatusDates);
  private _queuedProjectSubstatusDateUpdates: any[] = [];
  private _datesSaving: number = 0;
  private datesSaving: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this._datesSaving > 0
  );
  constructor(public apollo: Apollo, private _snackBar: MatSnackBar) {}

  public setInitialData(
    projectSubstatuses: any[],
    projectSubstatusDates: any[],
    subsequences: any[]
  ) {
    this._projectSubstatusDates = structuredClone(projectSubstatusDates);
    this.projectSubstatusDates.next(this._projectSubstatusDates);
    this._projectSubstatuses = structuredClone(projectSubstatuses);
    this.projectSubstatuses.next(this._projectSubstatuses);
    this._subsequences = structuredClone(subsequences);
  }
  filterActiveSubsequences(currentStatus: string | null, subsequence: any) {
    return subsequence.substatuses.find((substatus: any) =>
      substatus.statusSubstatuses.find(
        (statusSubstatus: any) => statusSubstatus.status_id === currentStatus
      )
    );
  }
  mapIndicesToSubstatuses(currentStatus: string | null, subsequence: any) {
    subsequence.substatuses.map((substatus: any) => {
      substatus.index = substatus.statusSubstatuses.find(
        (statusSubstatus: any) => statusSubstatus.status_id === currentStatus
      )?.index;
      return substatus;
    });
    return subsequence;
  }
  getProjectSubstatuses(): Observable<any[]> {
    return this.projectSubstatuses.asObservable();
  }
  getActiveSequences(currentStatus: string | null): Observable<any[]> {
    if (currentStatus) {
      if (!this.activeSequences[currentStatus]) {
        this.activeSequences[currentStatus] = new BehaviorSubject<any[]>(
          this._subsequences
            .filter((value: any) =>
              this.filterActiveSubsequences(currentStatus, value)
            )
            .slice()
            .map((value: any) =>
              this.mapIndicesToSubstatuses(currentStatus, value)
            )
        );
      }
      return this.activeSequences[currentStatus].asObservable();
    }
    this.activeSequences[''] = new BehaviorSubject<any[]>([]);
    return this.activeSequences[''].asObservable();
  }
  getSubSequenceCurrentIndices() {
    return;
  }
  updateDatesSaving() {
    this.datesSaving.next(this._datesSaving > 0);
  }
  getDatesSaving() {
    return this.datesSaving.asObservable();
  }
  getProjectSubstatusDates() {
    return this.projectSubstatusDates.asObservable();
  }
  getSavingProjectSubstatuses() {
    return this.savingProjectSubstatuses.asObservable();
  }
  deleteProjectSubstatusDates(id: any, subsequence_id: any, substatus_id: any) {
    this._queuedProjectSubstatusDateUpdates =
      this._queuedProjectSubstatusDateUpdates.filter(
        (exDate: any) =>
          !(
            exDate.subsequence_id === subsequence_id &&
            exDate.substatus_id === substatus_id
          )
      );
    this._datesSaving += 1;
    this.updateDatesSaving();
    let sub = this.apollo
      .mutate({
        mutation: DELETE_SUBSTATUS_DATE,
        variables: {
          id,
        },
      })
      .subscribe((data: any) => {
        if (data?.data?.deleteProjectSubstatusDate?.id) {
          this._projectSubstatusDates = this._projectSubstatusDates.filter(
            (projectSubstatusDate: any) =>
              projectSubstatusDate.id !==
              data?.data?.deleteProjectSubstatusDate?.id
          );
          this.projectSubstatusDates.next(this._projectSubstatusDates);
          if (this._datesSaving > 0) {
            this._datesSaving--;
          }
          this.updateDatesSaving();
          this.openSnackBar('Deleted Substatus Dates');
          sub?.unsubscribe();
        }
      });
  }
  upsertProjectSubstatusDates() {
    let subs: HashMap<Observable<any>> = {};
    this._datesSaving += this._queuedProjectSubstatusDateUpdates.length;
    this.updateDatesSaving();
    for (let i = 0; i < this._queuedProjectSubstatusDateUpdates.length; i++) {
      let date = this._queuedProjectSubstatusDateUpdates[i];
      subs[`save${i},`] = this.apollo.mutate({
        mutation: UPDATE_SUBSTATUS_DATE,
        variables: {
          id: date.id,
          input: {
            project_status_id: date.project_status_id,
            subsequence_id: date.subsequence_id,
            utc_time: date.time ? date.time : date.utc_time,
            timezone: date.timezone,
            substatus_id: date.substatus_id,
            date: moment(date.date).format('YYYY-MM-DD'),
            target: date.target,
          },
        },
      });
    }
    forkJoin(subs).subscribe((data: any) => {
      let keys = Object.keys(data);
      for (let i = 0; i < keys.length; i++) {
        let obj = data[keys[i]];
        if (obj.data?.upsertProjectSubstatusDate?.id) {
          let index = this._projectSubstatusDates.findIndex(
            (substatusDate: any) =>
              substatusDate.id === obj.data.upsertProjectSubstatusDate.id
          );
          if (index > -1) {
            this._projectSubstatusDates[index] = {
              ...obj.data.upsertProjectSubstatusDate,
            };
          } else {
            this._projectSubstatusDates.push({
              ...obj.data.upsertProjectSubstatusDate,
            });
          }
        }
        if (this._datesSaving > 0) {
          this._datesSaving--;
        }
        this.updateDatesSaving();
      }
      this.projectSubstatusDates.next(this._projectSubstatusDates);
      this.openSnackBar('Saved substatus dates');
    });
    this._queuedProjectSubstatusDateUpdates = [];
  }
  queryProjectSubstatusDates(project_status_id: number) {
    let sub = this.apollo
      .query({
        query: GET_SUBSTATUS_DATES,
        variables: {
          project_status_id,
        },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        let { projectSubstatusDates } = data as {
          projectSubstatusDates: ProjectSubstatusDate[];
        };
        if (projectSubstatusDates) {
          let newArray = this._projectSubstatusDates.filter(
            (date) => date.project_status_id !== project_status_id
          );
          this._projectSubstatusDates = structuredClone([
            ...newArray,
            ...projectSubstatusDates,
          ]);
          this.projectSubstatusDates.next(this._projectSubstatusDates);
        }
        sub?.unsubscribe();
      });
  }

  generateDummyDate(statusObj:any) {
    return {
      id: null,
      date: '',
      time: '',
      timezone: '',
      subsequence_id: statusObj.subsequence_id ?? '',
      substatus_id: statusObj.substatus_id ?? '',
      project_status_id: statusObj.project_status_id ?? '',
      target: false,
    };
  }
  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }
  queueProjectSubstatusDateUpdate(date: any) {
    let existingDate = this._queuedProjectSubstatusDateUpdates.findIndex(
      (exDate: any) =>
        exDate.substatus_id === date.substatus_id &&
        exDate.subsequence_id === exDate.subsequence_id
    );
    if (existingDate > -1) {
      this._queuedProjectSubstatusDateUpdates[existingDate] = date;
    } else {
      this._queuedProjectSubstatusDateUpdates.push(date);
    }
  }
  upsertProjectSubstatus(
    substatus: any,
    subsequence: any,
    project_status_id: any
  ) {
    this.savingProjectSubstatuses.next(true);
    let projectSubstatusIndex = this._projectSubstatuses.findIndex(
      (projectSubstatus: any) =>
        projectSubstatus.subsequence_id === subsequence.id &&
        projectSubstatus.project_status_id === project_status_id
    );
    let projectSubstatus = this._projectSubstatuses[projectSubstatusIndex];
    let input = {
      substatus_id: substatus.id,
      subsequence_id: subsequence.id,
      project_status_id: project_status_id,
    };
    if (projectSubstatus?.substatus_id === substatus.id) {
      input.substatus_id = null;
    }
    let sub = this.apollo
      .mutate({
        variables: {
          id: projectSubstatus?.id,
          input,
        },
        mutation: UPDATE_SUBSTATUS,
      })
      .subscribe((data: any) => {
        this.savingProjectSubstatuses.next(false);
        if (data?.data?.upsertProjectSubstatus) {
          if (this._projectSubstatuses[projectSubstatusIndex]) {
            this._projectSubstatuses[projectSubstatusIndex] =
              data.data.upsertProjectSubstatus;
          } else {
            this._projectSubstatuses.push(data.data.upsertProjectSubstatus);
          }
          this.queryProjectSubstatusDates(project_status_id);
          this.projectSubstatuses.next(this._projectSubstatuses);
        }
        this.openSnackBar('Updated substatus');
        sub && sub.unsubscribe();
      });
  }
}
